import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";

import { formatVideoName } from "../../admin/videos/hooks/useFetchVideos";
import { PublicShareContainer } from "./components/PublicShareContainer";
import { DownloadVideoModal } from "../../../components/MenuOptions";
import { DownloadDropdown } from "../../../components/DownloadDropdown";
import { DownloadOverlay } from "../../../components/DownloadOverlay";
import { DotLottleLoader } from "../../../components/Loader/DotLottleLoader";
import axiosInstant from "../../../api";
import { getBrandOrder } from "../../../api/endpoints";
import {
  DownloadType,
  getBrandAllowedFileTypes,
} from "../../admin/videos/selectedVideoCardV2/helpers";
import { ViewToggle } from "../../../components/ViewToggle/ViewToggle";
import { VideoCard } from "../../../components/videoSection/VideoCard";
import { UploadableVideoCard } from "../../../components/videoSection/VideoUpload";
import { useDownloads } from "../../../components/Downloads";
import { useUser } from "../../../hooks/useUser";
import { isInternal } from "../../../utils";
import { useCommonParams } from "../../../hooks/useCommonParams";
import { BrandOrderItem } from "../../orders";
import { BrandProject } from "../../videoProduction/components/constants";
import { filterRealFiles } from "../../videoProduction/helper";
import {
  fileToIVideo,
  getWebmUrl,
  buildChannelFolderResolver,
  buildProjectsFolderResolver,
  collectZipKeys,
  buildZipSuffix,
  submitZip,
  isTimeoutError,
} from "../../../utils/downloadHelpers";
import { PublicShare, PublicShareContext } from "../../../components/PublicShare/PublicShareContext";

export default function OrderSharePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role } = useUser();

  const [order, setOrder] = useState<BrandOrderItem | null>(null);
  const [loading, setLoading] = useState(true);

  const { view: _view, setView } = useCommonParams({ defaultView: "list" });
  const view = _view as "grid" | "list";

  // Video preview
  const [selectedVideo, setSelectedVideo] = useState<any | null>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoErrorShown, setVideoErrorShown] = useState(false);

  // Download All state
  const [showDownloadAllOptions, setShowDownloadAllOptions] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [zippingAll, setZippingAll] = useState(false);

  // Per-project download state
  const [showProjectDropdown, setShowProjectDropdown] = useState<number | null>(
    null,
  );
  const [downloadingProjectId, setDownloadingProjectId] = useState<
    number | null
  >(null);

  // Shared download result modal (fast path); long-running zips are handed
  // off to the global download tracker (top bar).
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadLink, setDownloadLink] = useState("");
  const { promote } = useDownloads();

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await axiosInstant.get(getBrandOrder(Number(id)));
        setOrder(response.data);
      } catch (error: any) {
        if (error.response?.status === 403 || error.response?.status === 404) {
          navigate("/404");
        } else {
          toast.error("Failed to load order");
          navigate("/404");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id, navigate]);

  // Staff and pre-approval brands zip unapproved videos as requested.
  const allowUnapprovedZip =
    isInternal(role) || !!(order as any)?.brand?.pre_approval_downloading;
  const allowedFileTypes = useMemo(
    () =>
      isInternal(role)
        ? null
        : getBrandAllowedFileTypes([order?.rmn_brand, order?.brand]),
    [role, order?.rmn_brand, order?.brand],
  );

  const handleDownloadAll = async (type: DownloadType = DownloadType.WEBM) => {
    const allFiles = (order?.projects ?? []).flatMap((p) =>
      filterRealFiles(p.projectFiles),
    );
    if (!allFiles.length) return;

    const baseName = formatVideoName(order?.name || "order");
    const zipFileName = `${baseName}${buildZipSuffix(type)}.zip`;
    const roomId = Math.random().toString(36).substring(2, 15);

    try {
      setDownloadingAll(true);
      setZippingAll(true);
      setDownloadLink("");
      setDownloadModalOpen(false);

      const loadingId = toast.loading("Zipping files, please wait...");
      const { files, hasContent } = await collectZipKeys(
        allFiles,
        type,
        allowedFileTypes,
        buildProjectsFolderResolver(order?.projects),
        allowUnapprovedZip,
      );
      toast.dismiss(loadingId);

      if (!hasContent) {
        toast.error("No content available to download.");
        setZippingAll(false);
        setDownloadingAll(false);
        return;
      }

      const fileName = await submitZip(
        files,
        zipFileName,
        roomId,
        type === DownloadType.ALL && order?.id ? { orderId: order.id } : undefined,
      );
      if (fileName) {
        setDownloadLink(fileName);
        setDownloadModalOpen(true);
      }
      setDownloadingAll(false);
      setZippingAll(false);
    } catch (error: any) {
      if (isTimeoutError(error)) {
        // Long-running zip: the global tracker's top bar takes over.
        promote(roomId, zipFileName);
      } else {
        toast.error(error.message ?? "Download failed");
      }
      setDownloadingAll(false);
      setZippingAll(false);
    }
  };

  const handleDownloadProject = async (
    project: BrandProject,
    type: DownloadType = DownloadType.WEBM,
  ) => {
    const files = filterRealFiles(project.projectFiles);
    if (!files.length) return;

    const baseName = formatVideoName(project.title || "project");
    const zipFileName = `${baseName}${buildZipSuffix(type)}.zip`;
    const roomId = Math.random().toString(36).substring(2, 15);

    try {
      setDownloadingProjectId(project.id);
      setDownloadLink("");
      setDownloadModalOpen(false);

      const loadingId = toast.loading("Zipping files, please wait...");
      const { files: zipEntries, hasContent } = await collectZipKeys(
        files,
        type,
        allowedFileTypes,
        buildChannelFolderResolver(project.content_channels),
        allowUnapprovedZip,
      );
      toast.dismiss(loadingId);

      if (!hasContent) {
        toast.error("No content available to download.");
        setDownloadingProjectId(null);
        return;
      }

      const fileName = await submitZip(
        zipEntries,
        zipFileName,
        roomId,
        type === DownloadType.ALL ? { projectId: project.id } : undefined,
      );
      if (fileName) {
        setDownloadLink(fileName);
        setDownloadModalOpen(true);
      }
      setDownloadingProjectId(null);
    } catch (error: any) {
      if (isTimeoutError(error)) {
        // Long-running zip: the global tracker's top bar takes over.
        promote(roomId, zipFileName);
      } else {
        toast.error(error.message ?? "Download failed");
      }
      setDownloadingProjectId(null);
    }
  };

  // Per project: a video's comments come through the share page's own
  // order-id route (the API never sends this page the order's token).
  const shareByProject = useMemo(() => {
    const map = new Map<number, PublicShare>();
    if (order?.id) {
      ((order?.projects ?? []) as any[]).forEach((p) =>
        map.set(p.id, { orderId: order.id, base: "brand-orders", projectId: p.id }),
      );
    }
    return map;
  }, [order]);

  if (loading) {
    return (
      <PublicShareContainer title="Loading...">
        <Box className="flex justify-center items-center min-h-screen">
          <DotLottleLoader />
        </Box>
      </PublicShareContainer>
    );
  }

  if (!order) return null;

  const allProjects = (order.projects ?? []) as unknown as BrandProject[];
  const totalVideos = allProjects.reduce(
    (sum, p) => sum + filterRealFiles(p.projectFiles).length,
    0,
  );
  const isBusy = downloadingAll || zippingAll || downloadingProjectId !== null;

  const descriptionContent = (
    <>
      {order.brand && (
        <Typography variant="body2" color="darkgray1">
          Brand: {order.brand.name}
        </Typography>
      )}
      {order.sow && (
        <Typography variant="body2" color="darkgray1">
          SOW: {order.sow.name}
        </Typography>
      )}
    </>
  );

  return (
    <>
      <DownloadOverlay visible={zippingAll || downloadingProjectId !== null} />

      <PublicShareContainer
        title={order.name}
        descriptionContent={descriptionContent}
      >
        <Box className="flex flex-col gap-8">
          {/* Page header with Download All */}
          {totalVideos > 0 && (
            <Box className="flex justify-between items-center">
              <Box className="flex items-center gap-3">
                <Typography variant="h5" fontWeight={600} sx={{ fontSize: 32 }}>
                  Projects
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="mt-1"
                >
                  {allProjects.length} project
                  {allProjects.length !== 1 ? "s" : ""} &middot; {totalVideos}{" "}
                  video{totalVideos !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <Box className="flex items-center gap-3">
                <ViewToggle
                  view={view}
                  onViewChange={(v) => setView(v as "grid" | "list")}
                  gridValue="grid"
                  listValue="list"
                />
                <DownloadDropdown
                  isOpen={showDownloadAllOptions}
                  isDownloading={isBusy}
                  onToggle={() => setShowDownloadAllOptions((v) => !v)}
                  onSelect={(type) => {
                    handleDownloadAll(type);
                    setShowDownloadAllOptions(false);
                  }}
                  onClickAway={() => setShowDownloadAllOptions(false)}
                  allowedFileTypes={allowedFileTypes}
                />
              </Box>
            </Box>
          )}

          {/* Projects list */}
          {allProjects.length === 0 ? (
            <Box className="flex justify-center items-center py-12">
              <Typography variant="body1" color="textSecondary">
                No projects available for this order.
              </Typography>
            </Box>
          ) : (
            allProjects.map((project) => {
              const files = filterRealFiles(project.projectFiles);

              return (
                <PublicShareContext.Provider key={project.id} value={shareByProject.get(project.id) || null}>
                <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px !important", "&:before": { display: "none" } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box className="flex items-center justify-between w-full pr-2">
                      <Box className="flex items-center gap-3">
                        <Typography variant="h6" fontWeight={600}>
                          {project.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {files.length} video{files.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                      {files.length > 0 && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <DownloadDropdown
                            isOpen={showProjectDropdown === project.id}
                            isDownloading={downloadingProjectId === project.id}
                            onToggle={() =>
                              setShowProjectDropdown(
                                showProjectDropdown === project.id
                                  ? null
                                  : project.id,
                              )
                            }
                            onSelect={(type) => {
                              handleDownloadProject(project, type);
                              setShowProjectDropdown(null);
                            }}
                            onClickAway={() => setShowProjectDropdown(null)}
                            allowedFileTypes={allowedFileTypes}
                          />
                        </div>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {files.length > 0 ? (
                      <Box
                        className={
                          view === "list"
                            ? "flex flex-col gap-3"
                            : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                        }
                      >
                        {files.map((file: any) => (
                          /* Drag-only, matching the pre-redesign card (role-only gate,
                             no visible control). The order share payload does carry
                             `hash`, so the upload path can name the file correctly. */
                          <UploadableVideoCard
                            key={file.id}
                            video={fileToIVideo(file)}
                            project={project}
                            refetch={() => undefined}
                          >
                            {() => (
                              <VideoCard
                                video={fileToIVideo(file)}
                                variant={view === "list" ? "row" : "share"}
                                isShare
                                onOpen={() => {
                                  setSelectedVideo(file);
                                  setVideoModalOpen(true);
                                  setVideoErrorShown(false);
                                }}
                                project={project}
                              />
                            )}
                          </UploadableVideoCard>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No videos available for this project.
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
                </PublicShareContext.Provider>
              );
            })
          )}
        </Box>
      </PublicShareContainer>

      {/* Video preview dialog */}
      <Dialog
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={() => setVideoModalOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedVideo && (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                backgroundColor: "#000",
              }}
            >
              {(() => {
                const videoLocation = selectedVideo.location;
                const webmUrl = getWebmUrl(videoLocation);
                return (
                  <video
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                    style={{ width: "100%", height: "auto", display: "block" }}
                    crossOrigin="anonymous"
                    onError={(e: any) => {
                      const vid = e.target as HTMLVideoElement;
                      if (
                        vid.error &&
                        vid.error.code ===
                          vid.error.MEDIA_ERR_SRC_NOT_SUPPORTED &&
                        vid.readyState === 0 &&
                        !videoErrorShown
                      ) {
                        setVideoErrorShown(true);
                        toast.error(
                          "Failed to load video. Please try downloading it instead.",
                        );
                      } else if (
                        vid.error &&
                        vid.error.code === vid.error.MEDIA_ERR_NETWORK &&
                        !videoErrorShown
                      ) {
                        setVideoErrorShown(true);
                        toast.error(
                          "Network error loading video. Please try again.",
                        );
                      }
                    }}
                    onLoadStart={() => setVideoErrorShown(false)}
                    onCanPlay={() => setVideoErrorShown(false)}
                    onLoadedData={() => setVideoErrorShown(false)}
                    onPlaying={() => setVideoErrorShown(false)}
                  >
                    {videoLocation && (
                      <source
                        src={videoLocation}
                        type={`video/${
                          videoLocation.substring(
                            videoLocation.lastIndexOf(".") + 1,
                          ) || "mp4"
                        }`}
                      />
                    )}
                    {webmUrl && webmUrl !== videoLocation && (
                      <source src={webmUrl} type="video/webm" />
                    )}
                    Your browser does not support the video tag.
                  </video>
                );
              })()}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <DownloadVideoModal
        open={downloadModalOpen}
        onClose={() => {
          setDownloadModalOpen(false);
          setDownloadLink("");
        }}
        downloadSource={downloadLink}
      />
    </>
  );
}
